
import { userService } from '../../services';
import { request, success, failure, getApiErrorMessage, getMessage, isExpiredRegisterTokenMessage, OTP_EXPIRED_CREATE_ACCOUNT_MESSAGE } from '../../helpers';
import { authenticationTypes, userTypes } from '../types';
import { appActions } from './app.actions';
import { setAuthToken } from '../../helpers/request.helper';
import { setTenantId } from '../../utils/requestHeaders';
import { AUTH_USER_TYPES } from '../../constants/auth.constants';
import {
    saveAuthSession,
    clearAuthSession,
    pickAccessToken,
    pickTenantApiToken,
    withIamTokenPrefix,
    pickRefreshToken,
    pickTokenExpiresAt,
    pickUserId,
} from '../../helpers/authStorage.helper';
import { logAuthDebug, clearAuthDebug } from '../../helpers/authDebug.helper';
import { resolveUserNames } from '../../helpers/userName.helper';

const unwrapLoginPayload = (data = {}) => {
    if (typeof data === "string" && data.trim()) {
        return { token: data.trim() };
    }

    if (!data || typeof data !== "object") return {};

    if (typeof data.data === "string" && data.data.trim()) {
        return { ...data, token: data.data.trim() };
    }

    const inner = [data.data, data.result, data.payload].find(
        (value) => value && typeof value === "object" && !Array.isArray(value)
    );
    if (!inner) return data;

    if (
        inner.accessToken ||
        inner.accesstoken ||
        inner.access_token ||
        inner.token ||
        inner.authToken ||
        inner.authorizationToken ||
        inner.user ||
        inner.idToken
    ) {
        return {
            ...inner,
            headerToken: data.headerToken || inner.headerToken,
            token: inner.token || data.token || data.headerToken,
        };
    }

    return { ...data, ...inner };
};

const normalizeLoginUser = (data = {}, { preferUuidToken = false } = {}) => {
    const payload = unwrapLoginPayload(data);
    const merged =
        payload?.user && typeof payload.user === "object"
            ? { ...payload, ...payload.user }
            : { ...payload };

    const userId = pickUserId(merged) || pickUserId(payload);
    const picker = preferUuidToken ? pickTenantApiToken : pickAccessToken;
    const accessToken = picker(merged) || picker(payload);
    const refreshToken = pickRefreshToken(merged) || pickRefreshToken(payload);
    const expiryInSecs = Number(
        merged.expiryInSecs ?? merged.expiresInSecs ?? merged.expires_in ?? ""
    );
    const tokenExpiresAt = pickTokenExpiresAt(merged) || pickTokenExpiresAt(payload);
    const names = resolveUserNames({
        ...merged,
        username:
            merged.username ||
            merged.userName ||
            merged.preferred_username ||
            payload.username ||
            payload.userName ||
            "",
    });

    return {
        ...merged,
        // Keep both casings — IAM login uses `accesstoken`.
        accessToken,
        accesstoken: accessToken,
        refreshToken,
        refreshtoken: refreshToken,
        expiryInSecs: Number.isFinite(expiryInSecs) ? expiryInSecs : null,
        tokenExpiresAt,
        userId,
        id: userId || merged.id || "",
        username: names.username,
        firstname: names.firstname,
        lastname: names.lastname,
        displayName: names.displayName,
        email: merged.email || "",
        mobile: merged.mobile || "",
    };
};

const login = (username, password, loginContext = { type: AUTH_USER_TYPES.SYSTEM }) => {
    return dispatch => {
        dispatch(request(authenticationTypes.LOGIN_REQUEST));

        const isSystem = loginContext.type !== AUTH_USER_TYPES.TENANT;
        const tenantId = isSystem ? null : (loginContext.tenantId || null);
        const clientId = isSystem ? null : (loginContext.clientId || null);
        const userType = isSystem ? AUTH_USER_TYPES.SYSTEM : AUTH_USER_TYPES.TENANT;

        if (!isSystem && !clientId) {
            dispatch(failure(
                authenticationTypes.LOGIN_FAILURE,
                `Client ID is not configured for ${loginContext.label || "this tenant"}.`
            ));
            return;
        }

        setTenantId(tenantId);

        const loginRequest = isSystem
            ? userService.systemUserLogin(username, password)
            : userService.tenantUserLogin(username, password, clientId);

        loginRequest
            .then((data) => {
                const user = normalizeLoginUser(data, { preferUuidToken: !isSystem });
                if (!user.username && username) {
                    user.username = username;
                }
                const accessToken = withIamTokenPrefix(user.accessToken, userType);
                user.accessToken = accessToken;
                user.accesstoken = accessToken;
                setAuthToken(accessToken);
                const authState = {
                    user,
                    userType,
                    tenantId,
                    activeTenantId: null,
                    activeTenantName: null,
                    roles: user.roles || data?.roles || [],
                    permissions: user.permissions || data?.permissions || [],
                };
                dispatch({
                    type: authenticationTypes.LOGIN_SUCCESS,
                    ...authState,
                });
                saveAuthSession(authState);
                logAuthDebug(authState, "IDP Login");
            })
            .catch((errorPayload) => {
                const message = getApiErrorMessage(errorPayload);
                dispatch(failure(authenticationTypes.LOGIN_FAILURE, message));
            });
    };
};

const systemUserLogin = (username, password) =>
    login(username, password, { type: AUTH_USER_TYPES.SYSTEM, tenantId: null });

const logout = () => {
    return (dispatch, getState) => {
        const userType = getState()?.authentication?.userType;
        const logoutRequest =
            userType === AUTH_USER_TYPES.TENANT
                ? userService.tenantUserLogout()
                : userService.systemUserLogout();

        const finish = () => {
            setAuthToken(null);
            setTenantId(null);
            clearAuthSession();
            clearAuthDebug();
            dispatch(success(authenticationTypes.LOGOUT));
            if (window?.location?.reload) {
                window.location.href = "/";
            }
        };

        logoutRequest.then(finish).catch(finish);
    };
};

const systemUserLogout = () => logout();

const setActiveTenant = ({ id, name } = {}) => {
    return (dispatch, getState) => {
        const activeTenantId = id || name || null;
        const activeTenantName = name || id || null;
        setTenantId(activeTenantId);
        dispatch({
            type: authenticationTypes.SET_ACTIVE_TENANT,
            activeTenantId,
            activeTenantName,
        });
        const auth = getState()?.authentication || {};
        saveAuthSession({
            ...auth,
            activeTenantId,
            activeTenantName,
        });
        logAuthDebug(
            { ...auth, activeTenantId, activeTenantName },
            "IDP Active Tenant"
        );
    };
};

const clearActiveTenant = () => setActiveTenant({ id: null, name: null });

const getAccountLinkingAuthToken = (username, password, clientId, responseType, redirectURL, cb = () => { }) => {
    return dispatch => {
        userService.getAccountLinkingAuthToken(username, password, clientId, responseType, redirectURL)
            .then(
                (data) => {
                    let authorizationToken = data.authorizationToken
                    let clientType = data.clientType
                    cb(authorizationToken, clientType)
                },
                ({error, code}) => {
                    dispatch(failure(userTypes.GET_ACCOUNT_LINKING_AUTH_TOKEN, error?.toString() ?? ""));
                    getMessage(`auth-token-${code}`, 
                        {remainingAttempts: error?.remainingAttempts ?? null, message: error?.message ?? ""}).then((msg) => {
                        dispatch(appActions.apiErrorMessage(msg));
                    })
                }
            );
    };
}

const getAccountLinkingAuthTokenViaOtp = (username, otpToken, clientId, responseType, redirectURL, cb = () => { }) => {
    return dispatch => {
        userService.getAccountLinkingAuthTokenViaOtp(username, otpToken, clientId, responseType, redirectURL)
            .then(
                (data) => {
                    cb(data.authorizationToken, data.clientType)
                },
                ({error, code}) => {
                    dispatch(failure(userTypes.GET_ACCOUNT_LINKING_AUTH_TOKEN, error?.toString() ?? ""));
                    getMessage(`auth-token-${code}`,
                        {remainingAttempts: error?.remainingAttempts ?? null, message: error?.message ?? ""}).then((msg) => {
                        dispatch(appActions.apiErrorMessage(msg));
                    })
                }
            );
    };
}

const getAccountLinkingAuthCode = (scope, accepted, authorizationToken, cb = () => { }, onTokenExpired) => {
    return dispatch => {
        userService.getAccountLinkingAuthCode(scope, accepted, authorizationToken)
            .then(
                res => {
                    let authorizationCode = res.authorizationCode
                    cb(authorizationCode)
                },
                ({ error, code }) => {
                    if ((code === 401 || code === 403) && typeof onTokenExpired === "function") {
                        onTokenExpired();
                    }
                    dispatch(failure(userTypes.GET_ACCOUNT_LINKING_AUTH_CODE, error?.message?.toString() ?? ""));
                    getMessage(`auth-code-${code}`).then((msg) => {
                        dispatch(appActions.apiErrorMessage(msg));
                    })
                }
            );
    };
}

const registerUser = (payload, cb = () => {}, onError = () => {}) => {
    return dispatch => {

        userService.registerUser(payload).then(
            (res) => {
                getMessage(`register-user-200`).then((msg) => {
                    dispatch(appActions.apiSuccessMessage(msg));
                })
                cb(res);
            },
            (err) => {
                const error = err?.error;
                const code = err?.code;
                const rawMessage =
                    (typeof error === "string" ? error : error?.message) ||
                    err?.message ||
                    "";
                const friendly = getApiErrorMessage(err);
                getMessage(`register-user-${code ?? "error"}`, {
                    message: rawMessage
                }).then((msg) => {
                    dispatch(
                        appActions.apiErrorMessage(
                            isExpiredRegisterTokenMessage(rawMessage) ||
                                isExpiredRegisterTokenMessage(friendly)
                                ? OTP_EXPIRED_CREATE_ACCOUNT_MESSAGE
                                : msg
                        )
                    );
                });
                onError(err);
            }
        );
    };
};

const resetPassword = (payload, cb = () => {}) => {
    return dispatch => {
        userService.resetPassword(payload).then(
            (res) => {
                getMessage(`reset-password-200`).then((msg) => {
                    dispatch(appActions.apiSuccessMessage(msg));
                });
                cb(res);
            },
            ({ error, code }) => {
                getMessage(`reset-password-${code ?? "error"}`, {
                    message: error?.message ?? ""
                }).then((msg) => {
                    dispatch(appActions.apiErrorMessage(msg));
                });
            }
        );
    };
};


export const userActions = {
    login,
    logout,
    systemUserLogin,
    systemUserLogout,
    setActiveTenant,
    clearActiveTenant,
    getAccountLinkingAuthToken,
    getAccountLinkingAuthTokenViaOtp,
    getAccountLinkingAuthCode,
    registerUser,
    resetPassword
}
